import {
  useCallback, useEffect, useRef, useState,
} from 'react';

import { normalizeApiMoments } from './utils';
import { DEFAULT_POLLING_INTERVAL } from './types';

/**
 * Fetches key moments for a given program and polls the backend at a fixed interval.
 *
 * Mirrors `useKeyMoments` from @dtvgo/progress-bar: only moments with
 * `momentStart <= Date.now()` are kept, then the last 60 are normalized.
 */
export function useKeyMoments(
  getKeyMoments,
  programId,
  enabled = true,
  pollingInterval = DEFAULT_POLLING_INTERVAL,
) {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  // eslint-disable-next-line unicorn/no-useless-undefined
  const [error, setError] = useState(undefined);
  // eslint-disable-next-line unicorn/no-useless-undefined
  const [errorStatusCode, setErrorStatusCode] = useState(undefined);
  // eslint-disable-next-line unicorn/no-useless-undefined
  const [selectedId, setSelectedId] = useState(undefined);

  const mountedRef = useRef(true);
  const lastMomentIdRef = useRef(0);

  const fetchMoments = useCallback(async () => {
    try {
      const response = await getKeyMoments(programId);
      const moments = response?.data ?? [];

      const recentMoments = [...moments]
        .filter((moment) => {
          const timestamp = Date.parse(moment.momentStart);
          return Number.isFinite(timestamp) && timestamp <= Date.now();
        })
        .sort((a, b) => Date.parse(a.momentStart) - Date.parse(b.momentStart))
        .slice(-60);

      if (!mountedRef.current) return;

      const lastId = recentMoments.at(-1)?.momentId ?? 0;
      if (lastId !== lastMomentIdRef.current) {
        lastMomentIdRef.current = lastId;
        setItems(normalizeApiMoments(recentMoments));
      }

      // eslint-disable-next-line unicorn/no-useless-undefined
      setError(undefined);
      // eslint-disable-next-line unicorn/no-useless-undefined
      setErrorStatusCode(undefined);
    } catch (error_) {
      if (!mountedRef.current) return;
      setError(error_ instanceof Error ? error_ : new Error('Failed to fetch key moments'));
      const status = (error_ && typeof error_ === 'object' && error_.response && typeof error_.response.status === 'number')
        ? error_.response.status
        : undefined;
      setErrorStatusCode(Number.isFinite(status) ? status : undefined);
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [getKeyMoments, programId]);

  useEffect(() => {
    mountedRef.current = true;
    lastMomentIdRef.current = 0;

    if (!enabled || !programId) {
      setIsLoading(false);
      // eslint-disable-next-line unicorn/no-useless-undefined
      setErrorStatusCode(undefined);
      return () => {};
    }

    setIsLoading(true);
    fetchMoments().catch(() => {});

    const intervalId = setInterval(() => { fetchMoments().catch(() => {}); }, pollingInterval);

    return () => {
      mountedRef.current = false;
      clearInterval(intervalId);
    };
  }, [enabled, programId, pollingInterval, fetchMoments]);

  // Reset when programId changes
  useEffect(() => {
    setItems([]);
    // eslint-disable-next-line unicorn/no-useless-undefined
    setSelectedId(undefined);
    // eslint-disable-next-line unicorn/no-useless-undefined
    setError(undefined);
    // eslint-disable-next-line unicorn/no-useless-undefined
    setErrorStatusCode(undefined);
    lastMomentIdRef.current = 0;
  }, [programId]);

  const select = useCallback((incidenceId) => {
    setSelectedId(incidenceId);
  }, []);

  return {
    items,
    isLoading,
    error,
    errorStatusCode,
    selectedId,
    select,
  };
}
